import { type CSSProperties, type FC, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ActionButton, ActionButtonIntent, Badge, DialogComponent, HStack, Modal, TriggerButton } from "widgets";
import { useOverlay } from "widgets/hooks";

import type { IInputTableQuerySpecWithContext } from "@/api/WebApiClient.ts";
import { createSelectionModel } from "@/app-utils/selection-model.ts";
import { type VariantAnalysisGroup } from "@/graphql.ts";
import { useGetOrganismVariantAnalysisQuery } from "@/modules/workflow/graphql/coordinate-lists.gql.js";
import { setGroupMutantGenomeIds } from "@/redux/modules/saved-group.ts";
import { createUniqueKeyFromTableSpec, getTableDataSlice, type ITableData } from "@/redux/modules/table-data.ts";
import { type IAppState } from "@/redux/reducer.ts";
import type { ReduxDispatch } from "@/redux/redux-utils.ts";
import { DATATABLE_ID_TYPE, ENTITY_TYPE, ORGANISM_TABLE_COLUMNS } from "@/settings/settings.ts";

import ColumnSelectionButton from "../core/ColumnSelectionButton.js";
import DataTableContainer from "../core/grid/DataTableContainer.js";

export interface VariantMutantGenomesSelectionDialogProps extends VariantMutantGenomesSelectionTriggerButtonProps {
  closeFn: () => void;
  onSave: (selectedIds: number[]) => void;
}

export const VariantMutantGenomesSelectionDialog: FC<VariantMutantGenomesSelectionDialogProps> = (props) => {
  const [initialSelection] = useState<number[]>((props.restrictionVariantGenomesIds ?? []) as number[]);

  const vcfMutantGenomesTableSpec: IInputTableQuerySpecWithContext = {
    id: props.variantAnalysisGroup?.id,
    idType: DATATABLE_ID_TYPE.ENTITY,
    queryType: ENTITY_TYPE.ANALYSIS_GROUP,
    resultType: ENTITY_TYPE.ORGANISM,
    context: "tableParams",
  };

  const vcfMutantGenomesTableData: ITableData = useSelector<IAppState, ITableData>((state) =>
    getTableDataSlice(state.tableData, vcfMutantGenomesTableSpec),
  );

  return (
    <DialogComponent
      title={"Variant Genomes"}
      subtitle={"Select the mutant genomes to show"}
      mainButton={{
        label: "Select",
        onPress: () => {
          props.onSave(vcfMutantGenomesTableData.selectionModel.selectedIds as number[]);
        },
      }}
      actions={[
        <ColumnSelectionButton
          portalContext={createUniqueKeyFromTableSpec(vcfMutantGenomesTableSpec)}
          key={"variant-mutant-genomes-selection-form-action-1"}
        />,
      ]}
      contentStyle={{ padding: 0, height: "100%", position: "relative", borderTop: "1px solid #d8e4ed" }}
      fullscreen
      closeFn={props.closeFn}
    >
      {/* @ts-expect-error Reported missing properties of this component are actually not required */}
      <DataTableContainer
        key={createUniqueKeyFromTableSpec(vcfMutantGenomesTableSpec)}
        tableSpec={vcfMutantGenomesTableSpec}
        tableInitializationOptions={{
          initialSelectionModel: createSelectionModel(initialSelection),
          sortExpression: ORGANISM_TABLE_COLUMNS.NAME,
          filterExpression: "isReference=='false'",
        }}
      />
    </DialogComponent>
  );
};

export interface VariantMutantGenomesSelectionTriggerButtonProps {
  variantAnalysisGroup?: { id: VariantAnalysisGroup["id"] };
  restrictionVariantGenomesIds?: number[];
}

export const VariantMutantGenomesSelectionTriggerButton: FC<VariantMutantGenomesSelectionTriggerButtonProps> = (
  props,
) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const { state, overlayProps, triggerProps } = useOverlay();
  const selectionCount: number = (props.restrictionVariantGenomesIds ?? []).length;

  return (
    <>
      <TriggerButton icon="visibility" label={"Genomes"} showLabel badge={selectionCount || "0"} {...triggerProps} />
      <Modal state={state} {...overlayProps}>
        <VariantMutantGenomesSelectionDialog
          {...props}
          closeFn={state.close}
          onSave={(ids) => {
            dispatch(setGroupMutantGenomeIds(ids));
            state.close();
          }}
        />
      </Modal>
    </>
  );
};

export interface VariantMutantGenomesSelectionActionButtonProps {
  onSave: (ids: number[]) => void;
  genomeId: number;
  restrictionVariantGenomesIds?: number[];
  style?: CSSProperties;
}

export const VariantMutantGenomesSelectionActionButton: FC<VariantMutantGenomesSelectionActionButtonProps> = (
  props,
) => {
  const { state, overlayProps, triggerProps } = useOverlay();
  const { data, loading } = useGetOrganismVariantAnalysisQuery({ variables: { orgId: props.genomeId } });

  const selectionCount = props.restrictionVariantGenomesIds?.length || 0;
  const analysisGroupId = data?.organism.variantAnalysisGroups?.[0]?.id;

  return (
    <>
      <ActionButton
        icon="visibility"
        {...triggerProps}
        isDisabled={loading || analysisGroupId == undefined}
        intent={ActionButtonIntent.Outlined}
        style={props.style}
      >
        <HStack>
          <span>Select Variant Genomes</span>
          <Badge label={selectionCount || "0"} inline />
        </HStack>
      </ActionButton>
      <Modal state={state} {...overlayProps}>
        <VariantMutantGenomesSelectionDialog
          {...props}
          variantAnalysisGroup={{ id: analysisGroupId! }}
          restrictionVariantGenomesIds={props.restrictionVariantGenomesIds}
          closeFn={state.close}
          onSave={(ids) => {
            props.onSave(ids);
            state.close();
          }}
        />
      </Modal>
    </>
  );
};
